import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  PoleColor,
  PoleColorsChoiceArray,
} from '../../../../model/sign/enums/pole-color';
import {
  SignFrame,
  SignFramesChoiceArray,
} from '../../../../model/sign/enums/sign-frame';
import {
  SignMeasurements,
  SignMeasurementsChoiceArray,
} from '../../../../model/sign/enums/sign-measurements';
import {
  SignPersonalizedTextType,
  SignPersonalizedTextTypesChoiceArray,
} from '../../../../model/sign/enums/sign-personalized-text-type';
import {
  SignPressType,
  fetchSignPressTypes,
} from '../../../../model/sign/enums/sign-press-type';
import { SignDetail } from '../../../../model/sign/sign-detail';
import { SignsControllerRequestService } from '../../../../services/api/signs-controller-request.service';
import { SubscriptionBasedComponent } from '../../../../subscription-helpers/subscription-based.component';
import Company from '../../../../model/company/company';
import { SignParticipatingCompany } from '../../../../model/sign/sign-participating-company';
import { SnackService } from '../../../../services/snack.service';
import {
  SignShapeType,
  SignShapeTypesChoiceArray,
} from '../../../../model/sign/enums/sign-shape-type';
import {
  SignPositionType,
  SignPositionTypesChoiceArray,
} from '../../../../model/sign/enums/sign-position-type';
import {
  SignType,
  SignTypesChoiceArray,
} from '../../../../model/sign/enums/sign-type';
import { SignDetailLocation } from '../../../../model/sign/sign-detail-location';
import { cloneDeep } from 'lodash';
import { ModalService } from '../../../../services/modal.service';
import { Router } from '@angular/router';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import {
  SignFrameColor,
  SignFrameColorsChoiceArray,
} from '../../../../model/sign/enums/sign-frame-color';
import { MaterialType } from '../../../../model/print-production/print-product-enums';
import { MaterialOptions } from '../../../../model/sign/enums/sign-material';

import { MatDialog } from '@angular/material/dialog';
import { InfoPrintProductionModalComponent } from '../../../../pages/products/detail/print-production/info-print-production-modal/info-print-production-modal.component';

@Component({
  selector: 'sign-detail',
  templateUrl: './sign-detail.component.html',
  styleUrls: ['./sign-detail.component.scss'],
})
export class SignDetailComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnChanges
{
  @Input()
  sign: SignDetail;
  @Output()
  signSaved = new EventEmitter<SignDetail>();

  signTypeChoices: (SignType | null)[];
  signMeasurementsChoices: (SignMeasurements | null)[];
  pressTypeChoices: (SignPressType | null)[];
  nbColorsChoices: number[] = [1, 2, 3, 4, 5];

  frameChoices: (SignFrame | null)[];
  frameColorChoices: (SignFrameColor | null)[];
  hideFrameColorSelect: boolean;

  poleColorChoices: (PoleColor | null)[];
  textTypeChoices: (SignPersonalizedTextType | null)[];
  shapeTypeChoices: (SignShapeType | null)[];
  positionTypeChoices: (SignPositionType | null)[];

  cachedLocation: SignDetailLocation;
  cachedWithoutStock: boolean;
  materialChoices: { key: string; value: string }[] = [];

  loading = false;
  error: string | null = null;

  constructor(
    private signsService: SignsControllerRequestService,
    private snackService: SnackService,
    private modalService: ModalService,
    private dialog: MatDialog,
    private router: Router
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    try {
      this.loading = true;
      this.signTypeChoices = cloneDeep(SignTypesChoiceArray);
      this.signTypeChoices.unshift(null);
      this.signMeasurementsChoices = cloneDeep(SignMeasurementsChoiceArray);
      this.signMeasurementsChoices.unshift(null);
      this.pressTypeChoices = await fetchSignPressTypes();
      if (!this.pressTypeChoices.includes(null)) {
        this.pressTypeChoices.unshift(null);
      }
      this.frameChoices = cloneDeep(SignFramesChoiceArray);
      this.frameChoices.unshift(null);
      this.frameColorChoices = cloneDeep(SignFrameColorsChoiceArray);
      this.frameColorChoices.unshift(null);
      this.poleColorChoices = cloneDeep(PoleColorsChoiceArray);
      this.poleColorChoices.unshift(null);
      this.textTypeChoices = cloneDeep(SignPersonalizedTextTypesChoiceArray);
      this.textTypeChoices.unshift(null);
      this.shapeTypeChoices = cloneDeep(SignShapeTypesChoiceArray);
      this.shapeTypeChoices.unshift(null);
      this.positionTypeChoices = cloneDeep(SignPositionTypesChoiceArray);
      this.positionTypeChoices.unshift(null);
      this.hideFrameColorSelect = this.sign?.frame?.label === 'Hout';
    } catch (err) {
      this.error = 'Failed to load sign press types';
      console.error('Error loading sign press types:', err);
    } finally {
      this.loading = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.sign) {
      if (this.sign != null) {
        this.supportCompanyPickerComponent();
        if (this.sign.defaultLocation != null) {
          this.cachedLocation = cloneDeep(this.sign.defaultLocation);
        } else {
          this.cachedLocation = null;
        }
        this.supportDefaultLocation();
        this.cachedWithoutStock = this.sign.withoutStock;
        this.hideFrameColorSelect = this.sign.frame?.label !== 'Hout';
        this.populateMaterialChoices();
      }
    }
  }

  private supportDefaultLocation(): void {
    if (this.sign.defaultLocation == null) {
      this.sign.defaultLocation = new SignDetailLocation();
    }
  }

  private unsetDefaultLocationValues(): void {
    if (this.sign.defaultLocation != null) {
      this.sign.defaultLocation.rack = null;
      this.sign.defaultLocation.shelf = null;
      this.sign.defaultLocation.compartment = null;
    }
  }

  private supportCompanyPickerComponent(): void {
    if (this.sign.participatingCompanies != null) {
      this.sign.participatingCompanies.forEach((pc) => {
        pc.localCompany = new Company();
        if (pc.company != null) {
          pc.localCompany.id = pc.company.id;
        }
      });
    }
  }

  // Generic comparison function
  compareWith<T extends { value: number }>(
    o1: T | null,
    o2: T | null
  ): boolean {
    if (o1 == null && o2 == null) {
      return true;
    } else if (o1 != null && o2 != null) {
      return o1.value === o2.value;
    } else {
      return false;
    }
  }

  // Specific comparison functions using the generic function
  comparePressType = this.compareWith<SignPressType>;
  compareSignMeasurements = this.compareWith<SignMeasurements>;
  compareFrame = this.compareWith<SignFrame>;
  compareFrameColor = this.compareWith<SignFrameColor>;
  comparePoleColor = this.compareWith<PoleColor>;
  compareSignType = this.compareWith<SignType>;
  compareTextType = this.compareWith<SignPersonalizedTextType>;
  comparePositionType = this.compareWith<SignPositionType>;
  compareShapeType = this.compareWith<SignShapeType>;

  private stockTransferRequired(): boolean {
    if (this.cachedLocation == null || this.sign.withoutStock) {
      return false;
    }
    //Beyond this comment: 'previous location' != null
    if (
      this.sign.defaultLocation.rack == null ||
      this.nullOrEmpty(this.sign.defaultLocation.shelf) ||
      this.sign.defaultLocation.compartment == null
    ) {
      //This is problematic...
      //Location used to be set and now is not. If Inpress does this for signs with existing stock,
      //some of the stock may not be displayable in their requested design.
      return false;
    }
    //Location will exist after save!
    if (
      this.sign.defaultLocation.rack === this.cachedLocation.rack &&
      this.sign.defaultLocation.shelf === this.cachedLocation.shelf &&
      this.sign.defaultLocation.compartment === this.cachedLocation.compartment
    ) {
      return false;
    }

    return true;
  }

  private transitioningToWithoutStock(): boolean {
    return (
      this.sign.id != null && this.sign.withoutStock && !this.cachedWithoutStock
    );
  }

  private nullOrEmpty(s: string): boolean {
    if (s == null || s.trim().length === 0) {
      return true;
    }
    return false;
  }

  save(): void {
    if (this.transitioningToWithoutStock()) {
      this.showSaveModal(
        'Bord zonder voorraad',
        'U staat op het punt dit bord met voorraad op te slaan als bord zonder voorraad. Dit betekent dat alle voorraad voor dit bord zal worden gewist. Bent u zeker dat u dit wilt?',
        GeneralHelper.noop
      );
    } else if (this.stockTransferRequired()) {
      this.showSaveModal(
        'Stock verplaatsing',
        'U heeft de standaard locatie aangepast. Bent u zeker dat u alle stock wilt verplaatsen naar deze locatie?',
        () => (this.sign.transferExistingStockToNewLocation = true)
      );
    } else {
      this.performSave();
    }
  }

  private showSaveModal(
    title: string,
    text: string,
    onConfirm: () => void
  ): void {
    this.modalService.confirmDialog(title, text, (b: boolean) => {
      if (b) {
        onConfirm();
        this.performSave();
      }
    });
  }
  private validateSignSaveConditions(): boolean {
    // If withoutStock is false, validate location
    if (!this.sign.withoutStock) {
      // Check if default location is fully specified
      const location = this.sign.defaultLocation;
      if (
        !location ||
        location.rack == null ||
        this.nullOrEmpty(location.shelf) ||
        location.compartment == null
      ) {
        this.snackService.showError(
          'Gelieve een volledige standaard locatie op te geven of "Zonder voorraad" te selecteren'
        );
        return false;
      }
    }
    return true;
  }

  private performSave(): void {
    // Validate save conditions first
    if (!this.validateSignSaveConditions()) {
      return;
    }

    if (this.sign.withoutStock) {
      this.unsetDefaultLocationValues();
      this.sign.minimumStock = null;
    } else {
      this.sign.singleBoardPrice = null;
    }

    //pre save operation to prevent DB errors, remove any unselected companies
    if (this.sign.participatingCompanies != null) {
      this.sign.participatingCompanies =
        this.sign.participatingCompanies.filter((pc) => pc.company != null);
    }

    if (this.sign.id && this.sign.id > 0) {
      this.signsService.update(this.sign).then((sign) => {
        this.sign = sign;
        if (sign.withoutStock) {
          this.cachedLocation = null;
        }
        this.supportCompanyPickerComponent();
        this.supportDefaultLocation();
        this.snackService.showSuccess('Succesvol opgeslagen');
        this.signSaved.emit(this.sign);
      });
    } else {
      this.signsService.create(this.sign).then((sign) => {
        this.sign = sign;
        if (sign.withoutStock) {
          this.cachedLocation = null;
        }
        this.supportCompanyPickerComponent();
        this.supportDefaultLocation();
        this.snackService.showSuccess('Succesvol opgeslagen');
        this.signSaved.emit(this.sign);
      });
    }
  }

  cancel(): void {
    this.router.navigateByUrl(`/signs-portal/signs`);
  }

  addParticipatingCompany(): void {
    if (this.sign.participatingCompanies == null) {
      this.sign.participatingCompanies = [];
    }

    const pc = new SignParticipatingCompany();
    pc.localCompany = new Company();

    this.sign.participatingCompanies.push(pc);
  }

  removeParticipatingCompanyAt(index: number): void {
    this.sign.participatingCompanies.splice(index, 1);
  }

  onPairedWithSelfChanged(): void {
    if (this.sign.pairedWithSelf === true) {
      this.sign.pairedSign = null;
    }
  }

  atleastOneCompanySelected(): boolean {
    return this.sign.participatingCompanies.some(
      (i) => i.company != null && i.company.id != null && i.company.id > 0
    );
  }

  onFrameChange() {
    if (this.sign && this.sign.frame && this.sign.frame.label === 'Hout') {
      this.hideFrameColorSelect = false;
    } else {
      this.hideFrameColorSelect = true;
      this.sign.frameColor = null; //reset color
    }
  }

  immoInfoChange(value: string): void {
    this.sign.immoInfo = value;
  }

  graphicalInfoChange(value: string): void {
    this.sign.graphicalInfo = value;
  }

  studioInfoChange(value: string): void {
    this.sign.studioInfo = value;
  }

  installerInfoChange(value: string): void {
    this.sign.installerInfo = value;
  }

  archive() {
    this.modalService.confirmDialog(
      'Bord verwijderen',
      'Bent u zeker dat u het bord wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.signsService.archive(this.sign.id).then(() => {
            this.snackService.showSuccess('Bord succesvol verwijderd');
            this.router.navigateByUrl(`/signs`);
          });
        }
      }
    );
  }

  populateMaterialChoices() {
    if (this.sign.type) {
      const materials = this.getAvailableMaterials(this.sign.type?.value);
      this.materialChoices = this.mapEnumToChoices(MaterialType).filter(
        (option) =>
          materials.includes(
            MaterialType[option.key as keyof typeof MaterialType]
          )
      );
    }
  }

  mapEnumToChoices(enumType: object) {
    return Object.entries(enumType).map(([key, value]) => ({
      key,
      value,
    }));
  }

  public getAvailableMaterials(signTypeValue: number): MaterialType[] {
    return MaterialOptions[signTypeValue] || [];
  }

  onTypeChange(): void {
    const materials = this.getAvailableMaterials(this.sign.type?.value);
    this.materialChoices = this.mapEnumToChoices(MaterialType).filter(
      (option) =>
        materials.includes(
          MaterialType[option.key as keyof typeof MaterialType]
        )
    );
  }

  displayPrintProductionInfo() {
    /*const dialogRef = */ this.dialog.open(InfoPrintProductionModalComponent, {
      data: this.sign.printProduction,
    });

    // dialogRef.afterClosed().subscribe((result) => {
    //
    // });
  }

  onSpecialShapeChange(): void {
    if (this.sign.specialShape?.value === true) {
      this.sign.descending = false;
    }
  }
}
